// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { JwtHelperService } from '@auth0/angular-jwt';
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// Model imports
import { WFEVendorParametersDto } from '../../workflow/_models/common/wfe-vendor-parameter-dto';
import { ManageVendorAccountsResultDto } from '../_models/manage-vendor-accounts-result-dto';
import { VendorAccountParametersDto } from '../_models/vendor-account-parameters-dto';
import { VendorAccountHistoryResultDto } from '../_models/vendor-account-history-result-dto';
import { SaveVendorAccountNotesParametersDto } from '../_models/save-vendor-account-notes-parameters-dto';
import { StatusMessageResultDto } from '../../_models/status-message-result-dto';
import { VendorAccountEditorResultDto } from '../_models/vendor-account-editor-result-dto';
import { VendorAccountRawDto } from '../_models/vendor-account-raw-dto';
import { VendorAccountEFTFormParametersDto } from '../_models/vendor-account-eft-form-parameters-dto';
import { VendorNotesResultDto } from '../_models/vendor-notes-result-dto';
import { ManageVendorAccountsParameterDto } from '../_models/manage-vendor-accounts-parameter-dto';
import { BaseVendorAccountParameterDto } from '../_models/base-vendor-account-parameter-dto';
import { BaseVendorAccountResultDto } from '../_models/base-vendor-account-result-dto';
import { VendorAccountHistoryParameterDto } from '../_models/vendor-account-history-parameter-dto';

// constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';

@Injectable({
    providedIn: 'root'
})
export class VendorAccountEditService {
    jwtHelper = new JwtHelperService();
    decodedToken: any;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getVendorAccountDetail(baseParams: WFEVendorParametersDto): Observable<ManageVendorAccountsResultDto> {
        const url = `${URLConstants.ccWorkflowEditorBaseApiUrl}vendor/account/manage${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<ManageVendorAccountsResultDto>(url, options)
            .pipe(map((response) => plainToClass(ManageVendorAccountsResultDto, response)));
    }

    getVendorAccountHistory(baseParams: VendorAccountParametersDto): Observable<VendorAccountHistoryResultDto[]> {
        const url = `${URLConstants.ccVendorAccountBaseApiUrl}/history${baseParams.getQueryString()}`;

        return this.httpClient
            .get<VendorAccountHistoryResultDto[]>(url)
            .pipe(map((response) => plainToClass(VendorAccountHistoryResultDto, response)));
    }

    getVendorAccountNotes(baseParams: VendorAccountParametersDto): Observable<VendorNotesResultDto> {
        const url = `${URLConstants.ccVendorAccountBaseApiUrl}/notes${baseParams.getQueryString()}`;

        return this.httpClient
            .get<VendorNotesResultDto>(url)
            .pipe(map((response) => plainToClass(VendorNotesResultDto, response)));
    }

    saveVendorAccountNotes(baseParams: SaveVendorAccountNotesParametersDto): Observable<any> {
        return this.httpClient
            .post<string>(`${URLConstants.ccVendorAccountBaseApiUrl}/notes`, classToPlain(baseParams))
            .pipe(map((responseData: any) => responseData));
    }

    getVendorAccountEditor(baseParams: VendorAccountParametersDto): Observable<VendorAccountEditorResultDto> {
        const url = `${URLConstants.ccVendorAccountBaseApiUrl}/editor${baseParams.getQueryString()}`;

        return this.httpClient
            .get<VendorAccountEditorResultDto>(url)
            .pipe(map((response) => plainToClass(VendorAccountEditorResultDto, response)));
    }

    saveVendorAccount(baseParams: VendorAccountRawDto): Observable<StatusMessageResultDto> {
        return this.httpClient
            .post<StatusMessageResultDto>(URLConstants.ccVendorAccountBaseApiUrl, classToPlain(baseParams))
            .pipe(map((responseData: any) => plainToClass(StatusMessageResultDto, responseData)));
    }

    getVendorAccountEFTForm(baseParams: VendorAccountEFTFormParametersDto): Observable<Blob> {
        const url = `${URLConstants.ccVendorAccountBaseApiUrl}/eftform${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);

        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getVendorAccount(baseParam: ManageVendorAccountsParameterDto): Observable<ManageVendorAccountsResultDto> {
        const url = `${URLConstants.ccWorkflowViewerBaseApiUrl}vendor/account/manage${baseParam.getQueryString()}`;

        return this.httpClient
            .get<ManageVendorAccountsResultDto>(url)
            .pipe(map((response) => plainToClass(ManageVendorAccountsResultDto, response)));
    }

    getVendorAccountList(baseParam: BaseVendorAccountParameterDto): Observable<BaseVendorAccountResultDto[]> {
        const url = `${URLConstants.ccVendorAccountBaseApiUrl}${baseParam.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any[]) => plainToClass(BaseVendorAccountResultDto, responseData)));
    }
}
